<script setup lang="ts">
    import { IChoiceAuditResponse } from './AuditModel';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { audit } from '../../ChoiceScreens/Audit/AuditApi';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import * as DateUtils from '../../lib/DateUtils';
    import * as ChoiceEnums from '../../Enums/ChoiceEnums';
    import * as UIEnums from '../../Enums/UiEnums';
    import { AuditAction, AuditActionDictionary, AuditActionModel } from "./AuditEnums";
    import { ICol, RenderModeType, ValueStrDelegate, CellStyleDelegate } from "../../lib/ChoiceTableHelpers";
    import SectionPanel from "../../components/SectionPanel.vue";
    import IntegerInput from '../../components/IntegerInput.vue';
    import DropDownButton from "../../components/DropDownButton.vue";
    import DropDownButtonItem from "../../components/DropDownButtonItem.vue";
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import _ from "lodash";

    const props = withDefaults(defineProps<{
        identifierId: number,
        screenName: string,
        privilege: number
    }>(), {
    });

    onMounted(async () => {
        await loadAudit(); 
    });

    const choiceAudit = reactive<{
        auditData?: IChoiceAuditResponse[],
        auditSearchCriteria:
        {
            maxRows?: number,
            auditTypeItems?: AuditActionModel[],
            selectedAuditType?: AuditAction,
            dateStart: DateUtils.NoTzDate,
            dateEnd: DateUtils.NoTzDate,
            viewChangedOnly: boolean
        }
    }>({
        auditSearchCriteria:
        {
            maxRows: 25,
            auditTypeItems: _.sortBy(Object.values(AuditActionDictionary), "order"),
            selectedAuditType: AuditAction.All,
            dateStart: DateUtils.getNoTzDateAdd(-2, UIEnums.DateDisplayFormat.NumericDate), 
            dateEnd: DateUtils.getNoTzToday(),
            viewChangedOnly: false
        }
    });

    async function loadAudit() {
        if (!props.identifierId && !props.screenName) {
            return;
        }
        const resp = await audit.getAuditInfo(props.identifierId, props.screenName); 
        choiceAudit.auditData = resp;
    }

    const visibleAuditData = computed(()=> 
        { 
            const rowsOfInterest = choiceAudit.auditData?.filter(item => 
            {
                const auditDate = DateUtils.toDate(item.auditDate) ?? new Date();
                return auditDate >= DateUtils.startOfDay(DateUtils.toDate(choiceAudit.auditSearchCriteria?.dateStart) ?? new Date()) &&
                    auditDate <= DateUtils.endOfDay(DateUtils.toDate(choiceAudit.auditSearchCriteria?.dateEnd) ?? new Date()) &&
                    changeFilterValue(item) &&
                    updateAuditTypeFilter(item);
            });

            return rowsOfInterest?.slice(0, choiceAudit.auditSearchCriteria.maxRows);            
        });

    const formatLogTypeDesc: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: UIEnums.TableRowType): string | undefined => {
        if (rowType === UIEnums.TableRowType.Body)
        {
             return getAuditTypeDesc(row?.auditType);
        }
    }

    const narrowWidth: CellStyleDelegate = (_col: ICol, _row: Record<string, any>, _rowType: UIEnums.TableRowType): Record<string, string> | undefined => {
        if (_rowType === UIEnums.TableRowType.Body)
        {
            const styles: Record<string, string> = {};
            styles["width"] = "100px";
            return styles;
        }
    }   

    const tableColumns = ref<ICol[]>([
        { Name: 'Identifier', DataField: 'identifierId', StyleDel: narrowWidth },
        { Name: 'Audit Date', DataField: 'auditDate', RenderMode: RenderModeType.DateTime }, 
        { Name: 'Audit Type', DataField: 'auditType', FormatValue: formatLogTypeDesc },
        { Name: 'Transaction Id', DataField: 'transactionId' },
        { Name: 'User', DataField: 'userId' },
        { Name: 'Description', DataField: 'detailDescription' },
        { Name: 'Old Value', DataField: 'oldValue' },
        { Name: 'New Value', DataField: 'newValue' }
    ]);
   
    function getAuditTypeDesc(value: string): string | undefined {
        switch (value) {
            case AuditAction.Delete:
                return "Delete";
            case AuditAction.Insert:
                return "Insert";
            case AuditAction.Print:
                return "Print";
            case AuditAction.Update:
                return "Update";
            case AuditAction.View:
                return "View";
            default:
                return value;
        }
    }

    function changeFilterValue(item: IChoiceAuditResponse) : boolean {
        if (choiceAudit.auditSearchCriteria.viewChangedOnly) {
            return item.oldValue != item.newValue;  
        }
        return true;
    }

    function updateAuditTypeFilter(item: IChoiceAuditResponse) : boolean {
        if (AuditAction.All === (choiceAudit.auditSearchCriteria.selectedAuditType ?? AuditAction.All)) {
            return true;
        }
        return item.auditType === choiceAudit.auditSearchCriteria.selectedAuditType;
    }

</script>

<template>
    <SectionPanel>
        <div class="row label-isalus-small align-items-center">
            <div class="col-md-1">Max Rows
                <IntegerInput 
                    v-model:numberValue="choiceAudit.auditSearchCriteria.maxRows" 
                    :maxLength=4 
                    :startingRange=1
                    :endingRange=1000
                    :positive=true
                    :required=false                    
                    :alignment=ChoiceEnums.Alignment.left
                    :cssFramework = "'vanilla'"
                /> 
            </div>
            <div class="col-md-1">Audit Type 
                <DropDownButton :size="UIEnums.ButtonDisplaySize.Small" :displayStyle="UIEnums.ButtonDisplayStyle.OutlineSecondary">
                    <template #button>{{choiceAudit.auditSearchCriteria.selectedAuditType ?? "All "}}</template>
                    <DropDownButtonItem style="font-size: .75rem;" v-for="auditItem in choiceAudit.auditSearchCriteria.auditTypeItems" @click="choiceAudit.auditSearchCriteria.selectedAuditType = auditItem.name">
                        {{ auditItem.name}}
                    </DropDownButtonItem>
                </DropDownButton>
            </div>
            <div class="col-md-2">Audit Start
                <DatePickerNoTZ v-model:value="choiceAudit.auditSearchCriteria.dateStart" :inputClassName="'small_date'" :clearable="false" :required="true" :display-format="UIEnums.DateDisplayFormat.NumericDate"/>
            </div>
            <div class="col-md-2">Audit End
                <DatePickerNoTZ v-model:value="choiceAudit.auditSearchCriteria.dateEnd" :inputClassName="'small_date'" :clearable="false" :required="true" :display-format="UIEnums.DateDisplayFormat.NumericDate"/>
            </div>
            <div class="col-md-2">
                <input class="form-check-input me-2" v-model="choiceAudit.auditSearchCriteria.viewChangedOnly" type="checkbox" title="Check to view only changes between old and new values">View Changes Only
            </div>
        </div>
    </SectionPanel>

    <div class="row pt-1 pb-1 pe-3 justify-content-end label-isalus-small">
        # of Records: {{ visibleAuditData?.length }} of {{ choiceAudit.auditData?.length }}
    </div>
    <div class="row pt-1">
        <ChoiceTable 
            :columns="tableColumns" 
            :data="visibleAuditData" 
            :show-filters="false" 
            :allowSorting="true"
            fontSize="smaller"
            key-value-data-member="props.identifierId">
        </ChoiceTable>  
    </div>
</template>